import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_Mp4ToWavConverter = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter',
			name: 'MP4 to WAV Converter Online — Extract Audio Instantly by Lift',
			description:
				'Convert MP4 to WAV online — upload your MP4 file and Lift extracts the audio as WAV in seconds. Lossless quality, no software needed. Try Lift now!',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#product' },
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#howto' },
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#faq' },
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#why-lift' },
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#explore-tools' },
				{ '@id': 'https://liftapp.ai/mp4-to-wav-converter#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#product',
			name: 'MP4 to WAV Converter Online',
			description:
				'Extract audio from MP4 files and save as WAV. Upload your MP4 file, choose WAV as output, and download the lossless audio in seconds.',
			url: 'https://liftapp.ai/mp4-to-wav-converter',
			fileFormat: [
				'audio/wav',
				'video/mp4',
				'video/quicktime',
				'video/x-msvideo',
				'video/webm',
				'video/x-matroska',
			],
			memoryRequirements: 'Max file size: 2GB',
			brand: { '@type': 'Brand', name: 'Lift' },
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your MP4 File',
					text: 'Drop in your MP4 file or any other supported video format — MOV, AVI, MKV, WEBM, FLV, and more.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Choose WAV as Output',
					text: 'Select WAV from dozens of audio and video output formats — MP3, AAC, FLAC, OGG, MOV, MKV, and more are also available.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Lift Extracts the Audio',
					text: 'Lift processes your MP4 file and extracts the audio track with lossless WAV encoding, preserving the full original quality.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download the WAV',
					text: 'Your WAV file is ready instantly — download it for playback, editing, audio production, or archiving.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift MP4 to WAV Converter?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift MP4 to WAV Converter is an online tool that extracts audio from MP4 files and saves it as WAV in seconds. Upload your MP4 file, choose WAV as output, and download the lossless result.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift MP4 to WAV Converter work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your MP4 file, select WAV as the output format, and Lift processes the conversion. Your WAV file is ready to download in seconds.',
					},
				},
				{
					'@type': 'Question',
					name: 'How long does MP4 to WAV conversion take?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Most conversions complete in seconds. Processing time depends on file size, but Lift is optimized to deliver results as fast as possible.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will the extracted WAV maintain audio quality?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. WAV is a lossless format. Lift extracts the audio track from your MP4 and saves it as uncompressed WAV, preserving the full original audio quality.',
					},
				},
				{
					'@type': 'Question',
					name: 'Is it safe to convert files with Lift?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift prioritizes your privacy — all files are processed securely and are not stored or shared after conversion. Your converted result is available only to you.',
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#why-lift',
			name: "Why Lift's MP4 to WAV Converter stands out",
			description:
				'Lossless audio extraction, intelligent encoding, cross-platform support, and budget-friendly pricing — Lift delivers professional-grade conversion.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Lossless WAV quality',
					description:
						'AI-powered technology extracts audio as uncompressed WAV for full original fidelity.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent encoding',
					description:
						'AI tailors the extraction to the source MP4 for optimal WAV output.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description:
						'Available on iOS, Android, and Web for seamless access.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly software with an affordable and intuitive tool.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/images/mp4-to-wav-converter/whyLift.webp',
				name: 'Lift MP4 to WAV Converter delivering lossless audio',
				description: "Lift's online MP4 to WAV converter in high resolution.",
			},
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/mp4-to-wav-converter#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'MP4 to WAV Converter',
					item: 'https://liftapp.ai/mp4-to-wav-converter',
				},
			],
		},
	],
};
